// Uses setTimeout with Promise to create an arbitrary delay time
async function returnValueInMacrotask<T>(returnValue: T): Promise<T> {
  return new Promise((resolve) => {
    setTimeout(() => resolve(returnValue), 0)
  })
}

/**
 * Promise.race in all of these functions uses delay of 0 to instantly resolve.
 *
 * If the promise is resolved or rejected, returning that value will beat the setTimeout in the race
 *
 * How this mechanism works (based on some event loop magic):
 * - If the promise is already settled, "then" callback is queued into a microtask queue.
 * - setTimeout always creates a macrotask (which is always executed after all the microtasks are done)
 * - So the already settled promise will always beat the setTimeout when being called this way
 */
export async function isPromiseSettled(promise: Promise<unknown>) {
  return Promise.race([
    returnValueInMacrotask(false),
    promise.then(
      () => true,
      () => true,
    ),
  ])
}

export async function isPromiseResolved(promise: Promise<unknown>) {
  return Promise.race([
    returnValueInMacrotask(false),
    promise.then(
      () => true,
      () => false,
    ),
  ])
}

export async function isPromiseRejected(promise: Promise<unknown>) {
  return Promise.race([
    returnValueInMacrotask(false),
    promise.then(
      () => false,
      () => true,
    ),
  ])
}
